import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Brain, Shield, Target, ArrowRight, Check, Zap, Users, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getPageContent, getServices } from '../utils/contentManager';

const Services = () => {
  const [pageContent, setPageContent] = useState({});
  const [services, setServices] = useState([]);

  useEffect(() => {
    const content = getPageContent('services');
    setPageContent(content);
    const servicesData = getServices();
    setServices(servicesData);
  }, []);

  const getIconComponent = (iconName) => {
    const icons = {
      Brain,
      Shield,
      Target,
      Zap,
      Users,
      TrendingUp
    };
    return icons[iconName] || Brain;
  };

  const process = [
    {
      step: '01',
      title: 'Discovery & Analysis',
      description: 'We dive deep into your business to understand challenges, goals, and opportunities.'
    },
    {
      step: '02',
      title: 'Strategy Development',
      description: 'Create a customized roadmap that aligns with your business objectives and timeline.'
    },
    {
      step: '03',
      title: 'Implementation',
      description: 'Execute the strategy with precision, ensuring seamless integration and minimal disruption.'
    },
    {
      step: '04',
      title: 'Optimization & Support',
      description: 'Continuous monitoring, optimization, and support to maximize long-term success.'
    }
  ];

  const additionalServices = [
    {
      icon: Zap,
      title: 'Digital Transformation',
      description: 'Complete digital overhaul to modernize your business operations and customer experience.'
    },
    {
      icon: Users,
      title: 'Team Training',
      description: 'Comprehensive training programs to ensure your team maximizes the potential of new systems.'
    },
    {
      icon: TrendingUp,
      title: 'Performance Analytics',
      description: 'Advanced analytics and reporting to track performance and identify growth opportunities.'
    }
  ];

  const features = [
    {
      icon: Brain,
      title: 'Agentic AI Solutions',
      description: 'Implement cutting-edge AI agents that autonomously handle complex business processes and decision-making.'
    },
    {
      icon: Shield,
      title: 'IT Consulting',
      description: 'Comprehensive technology strategy and implementation to modernize your business infrastructure.'
    },
    {
      icon: Target,
      title: 'Business Strategy',
      description: 'Data-driven strategic planning to accelerate growth and optimize operational efficiency.'
    }
  ];

  const stats = [
    { number: '50+', label: 'Clients Transformed' },
    { number: '200%', label: 'Average ROI Increase' },
    { number: '24/7', label: 'AI-Powered Support' },
    { number: '99.9%', label: 'System Uptime' }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      company: 'TechStart Inc.',
      text: 'OHA System revolutionized our operations with their AI solutions. We saw 300% productivity increase within 3 months.',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612786?w=100&h=100&fit=crop&crop=face'
    },
    {
      name: 'Michael Chen',
      company: 'GrowthCorp',
      text: 'Their business strategy consulting helped us scale from startup to $10M revenue. Exceptional expertise and results.',
      image: 'https://images.unsplash.com/photo-1472099645785-5654bff786?w=100&h=100&fit=crop&crop=face'
    },
    {
      name: 'Emily Rodriguez',
      company: 'InnovateLab',
      text: 'The AI implementation was seamless. Their team understood our needs and delivered beyond expectations.',
      image: 'https://images.unsplash.com/photo-14387616830333-6464ffad80?w=100&h=100&fit=crop&crop=face'
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 102, 255, 0.9), rgba(0, 51, 153, 0.9)), url('https://images.pexels.com/photos/7616608/pexels-photo-7616608.jpeg?w=1920&h=1080&fit=crop')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              {pageContent.title || 'Our'} <span className="text-indigo-200">Services</span>
            </h1>
            <p className="text-xl md:text-2xl max-w-4xl mx-auto leading-relaxed text-indigo-100">
              {pageContent.subtitle ||
                'Comprehensive solutions designed to transform your business through innovative technology and strategic consulting.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Choose Your Solution <span className="text-gradient"></span>
            </h2>
            <p className="text-xl md:text-2xl max-w-4xl mx-auto leading-relaxed text-indigo-100">
              {pageContent.subtitle ||
               'Comprehensive solutions designed to transform your business through innovative technology and strategic consulting.'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = getIconComponent(service.icon);
              return (
                <motion.div
                  key={service.id || index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2, duration: 0.8 }}
                  className={`relative bg-white p-8 rounded-2xl shadow-premium hover:shadow-glow transition-all duration-300 ${
                    service.popular ? 'ring-2 ring-blue-800' : ''
                  }`}
                >
                  {service.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="gradient-background text-white px-4 py-2 rounded-full text-sm font-semibold">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <div className="gradient-background w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                    <IconComponent size={32} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  <div className="space-y-3 mb-8">
                    {(service.features || []).map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-3">
                        <Check size={20} className="text-green-500" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                  {service.price && (
                    <div className="text-3xl font-bold text-gray-900 mb-6">{service.price}</div>
                  )}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-full gradient-background text-white py-3 rounded-xl font-semibold hover:shadow-glow transition-all duration-300 flex items-center justify-center"
                    onClick={() => window.location.href = '/contact'}
                  >
                    <span>Get Started</span>
                    <ArrowRight size={20} />
                  </motion.button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {pageContent.processTitle || 'Our'} <span className="text-gradient">Process</span>
            </h2>
            <p className="text-xl md:text-2xl max-w-4xl mx-auto leading-relaxed text-indigo-100">
              {pageContent.processSubtitle || 'A proven methodology that ensures successful outcomes for every project.'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2, duration: 0.8 }}
                className="text-center"
              >
                <div className="relative mb-8">
                  <div className="w-20 h-20 mx-auto gradient-background rounded-full flex items-center justify-center text-white text-2xl font-bold">
                    {step.step}
                  </div>
                  {index < process.length - 1 && (
                    <div className="hidden lg:block absolute top-10 left-full transform -translate-x-1/2 w-full h-1 bg-gray-200" />
                  )}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {pageContent.additionalTitle || 'Additional'} <span className="text-gradient">Services</span>
            </h2>
            <p className="text-xl md:text-2xl max-w-4xl mx-auto leading-relaxed text-indigo-100">
              {pageContent.additionalSubtitle || 'Complementary services to maximize your success and ensure comprehensive support.'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {additionalServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2, duration: 0.8 }}
                className="text-center"
              >
                <div className="gradient-background w-20 h-20 rounded-full flex items-center justify-center mb-6">
                  <service.icon size={40} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call To Action */}
      <section className="py-24 bg-indigo-700 text-white text-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold max-w-3xl mx-auto mb-6"
          >
            {pageContent.ctaTitle || 'Ready to Start Your AI Journey?'}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-lg max-w-2xl mx-auto mb-10"
          >
            {pageContent.ctaSubtitle || 'Let’s discuss how our AI-driven solutions can elevate your business to new heights.'}
          </motion.p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-block bg-white text-indigo-700 font-semibold px-10 py-4 rounded-lg shadow-lg hover:shadow-2xl"
            onClick={() => window.location.href = '/contact'} // redirect on click
          >
            Get Started
          </motion.button>
        </div>
      </section>
    </div>
  );
};

export default Services;
